import { useState } from 'react';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import {
  getSingleViewEditModes,
  getEditorMenuHighlightMode,
  normalizeEditorViewMode,
  getEditorViewModeLabel,
  viewModeToDisplayFlags,
  displayFlagsToViewMode,
  getAvailableDisplayFlags,
  resolveDisplayFlagsForTune,
} from '../viewModeUtils';
import { voiceDisplayLabel } from '../notation/notationDisplayAbc';
import {
  getTuneVoiceKeys,
  getVoiceViewSettings,
  setVoiceViewSettings,
} from '../abcVoiceViewSettings';
import { tuneHasExplicitChords } from '../timedLyricsChordsDisplay';
import useAbcjsParser from '../useAbcjsParser';
import { useIsNarrowViewport } from '../useMediaQuery';
import { isMobilePlatform } from '../platformUtils';
import DisplayModeControls from './DisplayModeControls';
import { NOTATION_FIT_VERTICAL } from '../gigNotationFit';

/** True when Fit height can apply to the current panels (hidden during file snapshot overlay). */
export function canShowFitHeightButton(flags, options) {
  if (options && options.fileOverlayActive) return false;
  if (!flags) return false;
  return flags.notation !== 'off' || !!flags.lyrics || !!flags.structure;
}

function fitHeightLabel(flags, vertical) {
  const notationOn = flags && flags.notation !== 'off';
  const lyricsOn = !!(flags && flags.lyrics);
  const structureOn = !!(flags && flags.structure);
  let target = 'content';
  if (notationOn) target = 'notation';
  else if (lyricsOn && !structureOn) target = 'lyrics';
  else if (structureOn && !lyricsOn) target = 'structure';
  else if (lyricsOn) target = 'lyrics';
  return vertical
    ? 'Fit ' + target + ' to page width'
    : 'Fit ' + target + ' to page height';
}

function NotationFitButton(props) {
  const { tunebook, fitMode, onChange, stopMenuClose, className, displayFlags } = props;
  const vertical = fitMode === NOTATION_FIT_VERTICAL;
  const label = fitHeightLabel(displayFlags, vertical);

  function stop(e) {
    if (!stopMenuClose) return;
    e.preventDefault();
    e.stopPropagation();
  }

  return (
    <Button
      size="sm"
      variant={vertical ? 'primary' : 'outline-secondary'}
      className={'notation-fit-btn' + (className ? ' ' + className : '')}
      aria-label={label}
      title={label}
      aria-pressed={vertical}
      onMouseDown={stop}
      onClick={function(e) {
        stop(e);
        if (onChange) {
          onChange(vertical ? 'horizontal' : NOTATION_FIT_VERTICAL);
        }
      }}
    >
      <span className="notation-fit-btn-icon">{tunebook.icons.fitvertical}</span>
      <span className="notation-fit-btn-label">Fit height</span>
    </Button>
  );
}

function renderEditorModeIcon(modeId, tunebook) {
  if (modeId === 'music') return tunebook.icons.music;
  if (modeId === 'pianoRoll') return tunebook.icons.pianoroll;
  if (modeId === 'notationAbc') {
    return <span className="editor-view-mode-text-icon" aria-hidden="true">ABC</span>;
  }
  if (modeId === 'sourceAbc') return tunebook.icons.filelist;
  if (modeId === 'chords') return tunebook.icons.guitar;
  if (modeId === 'info') return tunebook.icons.question;
  if (modeId === 'lyrics') return tunebook.icons.quillpen;
  return null;
}

export function ViewModeVoiceControls(props) {
  const { tune, tuneId, onChange, inline } = props;
  const voiceKeys = getTuneVoiceKeys(tune);

  if (!tuneId || voiceKeys.length <= 1) return null;

  const settings = getVoiceViewSettings(tuneId, voiceKeys);

  function toggleVoice(voiceKey) {
    const currentlyOn = settings.visible[voiceKey] !== false;
    const nextVisible = Object.assign({}, settings.visible);
    nextVisible[voiceKey] = !currentlyOn;
    const anyOn = voiceKeys.some(function(key) { return nextVisible[key] !== false; });
    if (!anyOn) return;
    const nextPlayable = Object.assign({}, settings.playable);
    voiceKeys.forEach(function(key) {
      nextPlayable[key] = nextVisible[key] !== false;
    });
    const saved = setVoiceViewSettings(tuneId, {
      visible: nextVisible,
      playable: nextPlayable,
    }, voiceKeys);
    if (onChange) onChange(saved);
  }

  return (
    <div
      className={'view-mode-voice-controls' + (inline ? ' view-mode-voice-controls--inline' : '')}
      onClick={function(e) { e.stopPropagation(); }}
      onMouseDown={function(e) { e.preventDefault(); e.stopPropagation(); }}
    >
      {!inline ? <div className="view-mode-voice-header">Voices</div> : null}
      <ButtonGroup size="sm" className="view-mode-voice-btn-group" aria-label="Voices">
        {inline ? (
          <Button
            variant="light"
            size="sm"
            className="view-mode-voice-group-label"
            disabled
            tabIndex={-1}
            aria-hidden="true"
          >
            Voices
          </Button>
        ) : null}
        {voiceKeys.map(function(voiceKey) {
          const label = voiceDisplayLabel(tune, voiceKey);
          const active = settings.visible[voiceKey] !== false;
          return (
            <Button
              key={voiceKey}
              variant={active ? 'primary' : 'outline-secondary'}
              className="view-mode-voice-btn"
              aria-label={active ? 'Hide ' + label : 'Show ' + label}
              title={label}
              aria-pressed={active}
              onClick={function(e) {
                e.preventDefault();
                e.stopPropagation();
                toggleVoice(voiceKey);
              }}
            >
              {label}
            </Button>
          );
        })}
      </ButtonGroup>
    </div>
  );
}

function DisplayModeToolbar(props) {
  const {
    displayFlags,
    available,
    tune,
    tunebook,
    onFlagsChange,
    onVoiceSettingsChange,
    notationFitMode,
    onNotationFitModeChange,
    className,
    hideInlineVoiceControls,
    separateInlineFitButton,
    embedFitInDisplayGroup,
    wrapFileControlsWithDividers,
    stopMenuClose,
    fileControls,
    fileOverlayActive,
    tablatureSelector,
  } = props;

  const notationOn = displayFlags && displayFlags.notation !== 'off';
  const showFit = canShowFitHeightButton(displayFlags, { fileOverlayActive: fileOverlayActive })
    && !!onNotationFitModeChange;
  const fitButton = showFit ? (
    <NotationFitButton
      tunebook={tunebook}
      fitMode={notationFitMode}
      onChange={onNotationFitModeChange}
      displayFlags={displayFlags}
      stopMenuClose={!!stopMenuClose}
    />
  ) : null;

  return (
    <div
      className={'display-mode-toolbar' + (className ? ' ' + className : '')}
      aria-label="View options"
    >
      <DisplayModeControls
        flags={displayFlags}
        available={available}
        tunebook={tunebook}
        onChange={onFlagsChange}
        appendControl={embedFitInDisplayGroup ? fitButton : null}
      />
      {fileControls ? (
        wrapFileControlsWithDividers ? (
          <>
            <Dropdown.Divider className="view-mode-file-controls-divider" />
            <div
              className="view-mode-file-controls"
              onClick={function(e) { e.stopPropagation(); }}
              onMouseDown={function(e) { e.stopPropagation(); }}
            >
              {fileControls}
            </div>
          </>
        ) : fileControls
      ) : null}
      {notationOn && !hideInlineVoiceControls ? (
        <ViewModeVoiceControls
          inline
          tune={tune}
          tuneId={tune && tune.id}
          tunebook={tunebook}
          onChange={onVoiceSettingsChange}
        />
      ) : null}
      {notationOn && tablatureSelector ? tablatureSelector : null}
      {showFit && !separateInlineFitButton && !embedFitInDisplayGroup ? fitButton : null}
    </div>
  );
}

function EditorViewModeToolbar(props) {
  const { currentMode, tunebook, onSelect, className } = props;
  const mobile = isMobilePlatform();
  const modes = getSingleViewEditModes({ mobile: mobile });
  const highlightMode = getEditorMenuHighlightMode(currentMode, mobile);

  return (
    <ButtonGroup
      className={'editor-view-mode-toolbar' + (className ? ' ' + className : '')}
      aria-label="Editor view"
    >
      {modes.map(function(mode) {
        const active = highlightMode === mode.id;
        const icon = renderEditorModeIcon(mode.id, tunebook);
        return (
          <Button
            key={mode.id}
            variant={active ? 'primary' : 'outline-secondary'}
            className="editor-view-mode-btn"
            aria-label={mode.label}
            title={mode.label}
            aria-pressed={active}
            onClick={function() { onSelect(mode.id); }}
          >
            {icon ? <span className="editor-view-mode-btn-icon">{icon}</span> : null}
            <span className="editor-view-mode-btn-label">{mode.label}</span>
          </Button>
        );
      })}
    </ButtonGroup>
  );
}

function EditorEditModeDropdown(props) {
  const { currentMode, tunebook, onSelect, className } = props;
  const [show, setShow] = useState(false);
  const mobile = isMobilePlatform();
  const modes = getSingleViewEditModes({ mobile: mobile });
  const highlightMode = getEditorMenuHighlightMode(currentMode, mobile);
  const label = getEditorViewModeLabel(currentMode, { mobile: mobile });
  const currentIcon = renderEditorModeIcon(highlightMode, tunebook);

  return (
    <Dropdown
      as={ButtonGroup}
      show={show}
      onToggle={function(next) { setShow(!!next); }}
      align="end"
      className={
        'editor-edit-mode-group editor-view-mode-selector'
        + (className ? ' ' + className : '')
      }
    >
      <Dropdown.Toggle
        variant="outline-secondary"
        className="editor-edit-mode-dropdown-toggle"
        id="editor-view-mode-dropdown"
        aria-label={'Edit view: ' + label}
        title={'Edit view: ' + label}
      >
        {currentIcon ? (
          <span className="editor-view-mode-btn-icon" aria-hidden="true">{currentIcon}</span>
        ) : null}
        <span className="view-mode-label">{label}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="music-actions-edit-submenu-menu"
        popperConfig={{
          strategy: 'fixed',
          modifiers: [
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport', padding: 8, altAxis: true },
            },
            {
              name: 'flip',
              options: { fallbackPlacements: ['top-end', 'bottom-start', 'top-start'] },
            },
          ],
        }}
      >
        {modes.map(function(mode) {
          const icon = renderEditorModeIcon(mode.id, tunebook);
          const active = highlightMode === mode.id;
          return (
            <Dropdown.Item
              key={mode.id}
              active={active}
              onClick={function() {
                onSelect(mode.id);
                setShow(false);
              }}
            >
              {icon ? <span className="view-mode-item-icon">{icon}</span> : null}
              {mode.label}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function ViewModeDisplayPanel(props) {
  const {
    displayFlags,
    available,
    tune,
    tunebook,
    onFlagsChange,
    onVoiceSettingsChange,
    notationFitMode,
    onNotationFitModeChange,
    hideInlineVoiceControls,
    fileControls,
    fileOverlayActive,
    tablatureSelector,
    extraMenuContent,
    afterDisplayModes,
    stopMenuClose,
  } = props;

  function stop(e) {
    if (!stopMenuClose) return;
    e.preventDefault();
    e.stopPropagation();
  }

  return (
    <div
      className="view-mode-display-panel px-2 py-2"
      onClick={stop}
      onMouseDown={stop}
    >
      <DisplayModeControls
        className="display-mode-controls--stacked"
        flags={displayFlags}
        available={available}
        tunebook={tunebook}
        onChange={onFlagsChange}
        stopMenuClose={!!stopMenuClose}
      />
      {afterDisplayModes ? (
        <>
          <Dropdown.Divider />
          <div className="view-mode-panel-slot px-1 py-1">{afterDisplayModes}</div>
        </>
      ) : null}
      {fileControls ? (
        <>
          <Dropdown.Divider />
          <div
            className="view-mode-file-controls px-1 py-1"
            onClick={stop}
            onMouseDown={stop}
          >
            {fileControls}
          </div>
        </>
      ) : null}
      {displayFlags.notation !== 'off' && !hideInlineVoiceControls && getTuneVoiceKeys(tune).length > 1 ? (
        <>
          <Dropdown.Divider />
          <ViewModeVoiceControls
            tune={tune}
            tuneId={tune && tune.id}
            tunebook={tunebook}
            onChange={onVoiceSettingsChange}
          />
        </>
      ) : null}
      {displayFlags.notation !== 'off' && tablatureSelector ? (
        <>
          <Dropdown.Divider />
          <div
            className="view-mode-tablature-selector"
            onClick={stop}
            onMouseDown={stop}
          >
            {tablatureSelector}
          </div>
        </>
      ) : null}
      {extraMenuContent ? (
        <>
          <Dropdown.Divider />
          <div
            className="view-mode-extra-menu-content"
            onClick={stop}
            onMouseDown={stop}
          >
            {extraMenuContent}
          </div>
        </>
      ) : null}
      {canShowFitHeightButton(displayFlags, { fileOverlayActive: fileOverlayActive }) && onNotationFitModeChange ? (
        <>
          <Dropdown.Divider />
          <NotationFitButton
            className="notation-fit-btn--menu"
            tunebook={tunebook}
            fitMode={notationFitMode}
            onChange={onNotationFitModeChange}
            displayFlags={displayFlags}
            stopMenuClose={!!stopMenuClose}
          />
        </>
      ) : null}
    </div>
  );
}

function ViewModeMenuSection(props) {
  const {
    displayFlags,
    available,
    tune,
    tunebook,
    onFlagsChange,
    onVoiceSettingsChange,
    notationFitMode,
    onNotationFitModeChange,
    hideInlineVoiceControls,
    fileControls,
    fileOverlayActive,
    tablatureSelector,
    extraMenuContent,
    afterDisplayModes,
    stopMenuClose,
  } = props;

  function stop(e) {
    if (!stopMenuClose) return;
    e.preventDefault();
    e.stopPropagation();
  }

  const showVoiceBelow = displayFlags.notation !== 'off'
    && !hideInlineVoiceControls
    && getTuneVoiceKeys(tune).length > 1;

  return (
    <div
      className="view-mode-menu-section"
      onClick={stop}
      onMouseDown={stop}
    >
      <DisplayModeToolbar
        className="display-mode-toolbar--menu"
        displayFlags={displayFlags}
        available={available}
        tune={tune}
        tunebook={tunebook}
        onFlagsChange={onFlagsChange}
        onVoiceSettingsChange={onVoiceSettingsChange}
        notationFitMode={notationFitMode}
        onNotationFitModeChange={onNotationFitModeChange}
        hideInlineVoiceControls={true}
        embedFitInDisplayGroup={true}
        wrapFileControlsWithDividers={true}
        stopMenuClose={!!stopMenuClose}
        fileControls={fileControls}
        fileOverlayActive={fileOverlayActive}
        tablatureSelector={tablatureSelector}
      />
      {showVoiceBelow ? (
        <>
          <Dropdown.Divider />
          <ViewModeVoiceControls
            tune={tune}
            tuneId={tune && tune.id}
            tunebook={tunebook}
            onChange={onVoiceSettingsChange}
          />
        </>
      ) : null}
      {afterDisplayModes ? (
        <>
          <Dropdown.Divider />
          <div className="view-mode-panel-slot px-1 py-1">{afterDisplayModes}</div>
        </>
      ) : null}
      {extraMenuContent ? (
        <>
          <Dropdown.Divider />
          <div className="view-mode-extra-menu-content">
            {extraMenuContent}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default function ViewModeSelectorModal(props) {
  const [show, setShow] = useState(false);
  const isEditor = props.variant === 'editor';
  const embeddedPanel = !!props.embeddedPanel;
  const isNarrowViewport = useIsNarrowViewport();
  const abcjsParser = useAbcjsParser({ tunebook: props.tunebook });
  const hasChords = !isEditor && !!props.tune
    && tuneHasExplicitChords(props.tune, props.tunebook, abcjsParser);
  const hasInfo = !isEditor && !!props.tune
    && typeof props.tune.backgroundInfo === 'string'
    && !!props.tune.backgroundInfo.trim();
  const available = !isEditor
    ? (props.availableOverride || getAvailableDisplayFlags(props.tune, props.tunebook, { hasChords: hasChords, hasInfo: hasInfo }))
    : null;
  const displayFlags = !isEditor
    ? resolveDisplayFlagsForTune(
      viewModeToDisplayFlags(props.viewMode),
      props.tune,
      props.tunebook,
      { hasChords: hasChords }
    )
    : null;
  const currentMode = isEditor
    ? normalizeEditorViewMode(props.viewMode)
    : displayFlagsToViewMode(displayFlags);
  const forceDropdown = !!props.forceDropdown;
  const useDisplayToolbar = !isEditor && !isNarrowViewport && !forceDropdown;
  const separateInlineFitButton = !isEditor && !!props.separateInlineFitButton;
  const fileOverlayActive = !!props.fileOverlayActive;
  const showSeparateInlineFitButton = separateInlineFitButton
    && canShowFitHeightButton(displayFlags, { fileOverlayActive: fileOverlayActive })
    && !!props.onNotationFitModeChange;

  function handleSelect(modeId) {
    props.onChange(modeId);
    setShow(false);
    if (props.closeParent) props.closeParent();
  }

  function handleFlagsChange(nextFlags) {
    const nextMode = displayFlagsToViewMode(nextFlags);
    if (nextMode === currentMode) return;
    props.onChange(nextMode);
  }

  if (isEditor) {
    if (!isNarrowViewport) {
      return (
        <EditorViewModeToolbar
          className={props.className}
          currentMode={currentMode}
          tunebook={props.tunebook}
          onSelect={handleSelect}
        />
      );
    }
    return (
      <EditorEditModeDropdown
        className={props.className}
        currentMode={currentMode}
        tunebook={props.tunebook}
        onSelect={handleSelect}
      />
    );
  }

  if (embeddedPanel) {
    return (
      <ViewModeMenuSection
        displayFlags={displayFlags}
        available={available}
        tune={props.tune}
        tunebook={props.tunebook}
        onFlagsChange={handleFlagsChange}
        onVoiceSettingsChange={props.onVoiceSettingsChange}
        notationFitMode={props.notationFitMode}
        onNotationFitModeChange={props.onNotationFitModeChange}
        hideInlineVoiceControls={props.hideInlineVoiceControls}
        fileControls={props.fileControls}
        fileOverlayActive={fileOverlayActive}
        tablatureSelector={props.tablatureSelector}
        extraMenuContent={props.extraMenuContent}
        afterDisplayModes={props.afterDisplayModes}
        stopMenuClose={props.stopMenuClose}
      />
    );
  }

  if (useDisplayToolbar) {
    return (
      <>
        <DisplayModeToolbar
          className={props.className}
          displayFlags={displayFlags}
          available={available}
          tune={props.tune}
          tunebook={props.tunebook}
          onFlagsChange={handleFlagsChange}
          onVoiceSettingsChange={props.onVoiceSettingsChange}
          notationFitMode={props.notationFitMode}
          onNotationFitModeChange={props.onNotationFitModeChange}
          hideInlineVoiceControls={props.hideInlineVoiceControls}
          separateInlineFitButton={separateInlineFitButton}
          fileControls={props.fileControls}
          fileOverlayActive={fileOverlayActive}
          tablatureSelector={props.tablatureSelector}
        />
        {showSeparateInlineFitButton ? (
          <NotationFitButton
            className="display-mode-toolbar-fit"
            tunebook={props.tunebook}
            fitMode={props.notationFitMode}
            onChange={props.onNotationFitModeChange}
            displayFlags={displayFlags}
          />
        ) : null}
      </>
    );
  }

  return (
    <Dropdown
      show={show}
      onToggle={function(next) { setShow(next); }}
      autoClose="outside"
      align="end"
      className={(props.className ? props.className + ' ' : '') + 'view-mode-selector'}
    >
      <Dropdown.Toggle
        variant="secondary"
        id="view-mode-dropdown"
        aria-label="View options"
        title="View options"
      >
        {props.tunebook.icons.eye}
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="view-mode-selector-menu"
        popperConfig={{
          strategy: 'fixed',
          modifiers: [
            {
              name: 'preventOverflow',
              options: { boundary: 'viewport', padding: 8, altAxis: true },
            },
            {
              name: 'flip',
              options: { fallbackPlacements: ['top-end', 'bottom-start', 'top-start'] },
            },
          ],
        }}
      >
        <ViewModeDisplayPanel
          displayFlags={displayFlags}
          available={available}
          tune={props.tune}
          tunebook={props.tunebook}
          onFlagsChange={handleFlagsChange}
          onVoiceSettingsChange={props.onVoiceSettingsChange}
          notationFitMode={props.notationFitMode}
          onNotationFitModeChange={props.onNotationFitModeChange}
          hideInlineVoiceControls={props.hideInlineVoiceControls}
          fileControls={props.fileControls}
          fileOverlayActive={fileOverlayActive}
          tablatureSelector={props.tablatureSelector}
          extraMenuContent={props.extraMenuContent}
          afterDisplayModes={props.afterDisplayModes}
          stopMenuClose={true}
        />
      </Dropdown.Menu>
    </Dropdown>
  );
}
